<template>
  <div class="home">
    <Banners />
    <v-row justify="center">
      <v-col
        :xl="6"
        :lg="8"
        :xs="10"
      >
        <v-row>
          <v-col>
            <h1>Bike Collection</h1>
            <p>Let's get started! Enter a few details below to check our availability.</p>
          </v-col>
        </v-row>

        <v-form
          ref="form"
          v-model="validForm"
        >
          <v-row>
            <v-col
              cols="12"
              sm="6"
            >
              <v-text-field
                :value="form.collectionPostcode"
                :disabled="collectionAddressData.addresses ? true : false"
                :rules="[v => !!v || 'Collection postcode is required']"
                maxlength="10"
                label="Collection Postcode"
                hint="The postcode where we should collect your bike from"
                background-color="white"
                autofocus
                validate-on-blur
                outlined
                required
                @keyup.enter="checkAvailability('collectionPostcode')"
                @input="updateBookingFormValue($event, 'collectionPostcode')"
              />

              <a
                v-if="!collectionAddressData.addresses"
                class="caption"
                @click.stop="chooseLocationDialog = true"
              >
                <!-- Having trouble? Choose your location from a map <v-icon x-small color="primary">mdi-map-marker</v-icon> -->
              </a>

              <ChooseLocationDialog
                :dialog.sync="chooseLocationDialog"
              />
            </v-col>

            <v-col
              cols="12"
              sm="6"
            >
              <v-select
                :value="form.bikesForRepair"
                :disabled="collectionAddressData.addresses ? true : false"
                :items="[1, 2, 3, 4, 5]"
                label="Bikes For Service / Repair"
                hint="How many bikes do you want us to collect for service / repair?"
                background-color="white"
                persistent-hint
                outlined
                required
                @change="updateBookingFormValue($event, 'bikesForRepair')"
              />
            </v-col>
          </v-row>

          <v-row class="pb-4">
            <v-col>
              <v-btn
                v-if="!collectionAddressData.addresses"
                :loading="loading"
                color="primary"
                large
                @click="checkAvailability('collectionPostcode')">
                Check Availability <v-icon small right>mdi-calendar-check</v-icon>
              </v-btn>

              <v-btn
                v-else
                text
                small
                dense
                outlined
                color="primary"
                @click="collectionAddressData = {}"
              >
                Choose a different postcode
              </v-btn>
            </v-col>
          </v-row>

          <v-alert
            v-if="collectionPostcodeError.errors.postcode.length > 0"
            outlined
            border="top"
            type="error"
          >
            {{ collectionPostcodeError.errors.postcode[0] }}
          </v-alert>

          <template v-if="collectionAddressData.addresses">
            <template v-if="collectionAddressData.collection_days.length > 0">
              <v-alert
                v-if="collectionAddressData.addresses"
                outlined
                border="top"
                type="success"
                class="mb-4"
              >
                <strong>Great News!</strong> We have slots available for your {{ form.bikesForRepair === 1 ? 'bike' : 'bikes' }}!
              </v-alert>

              <v-divider class="my-6" />

              <v-row>
                <v-col>
                  <h2 class="mb-4">Collection Address</h2>

                  <template v-if="!showCollectionAddressFields">
                    <v-select
                      label="Collection Address"
                      :value="form.collectionAddress"
                      :items="collectionAddressData.addresses"
                      :rules="[v => !!v || 'Please choose a collection address']"
                      background-color="white"
                      autofocus
                      required
                      outlined
                      dense
                      @change="updateBookingFormValue($event, 'collectionAddress')"
                    />
                    <a
                      v-if="!showCollectionAddressFields"
                      class="caption"
                      @click.stop="showCollectionAddressFields = true"
                    >
                      Having trouble? Enter your address manually <v-icon x-small color="primary">mdi-map</v-icon>
                    </a>
                  </template>
                  <template v-else>
                    <v-text-field
                      :value="form.collectionAddress"
                      :rules="[v => !!v || 'Collection adress is required']"
                      label="Collection Address"
                      placeholder="House / Flat Number, Building, Street, Postcode"
                      background-color="white"
                      autofocus
                      outlined
                      required
                      dense
                      @input="updateBookingFormValue($event, 'collectionAddress')"
                    />
                    <a
                      class="caption"
                      @click.stop="showCollectionAddressFields = false"
                    >
                      Choose address from dropdown <v-icon x-small color="primary">mdi-map-marker</v-icon>
                    </a>
                  </template>
                </v-col>
              </v-row>

              <h2 class="mt-6">Collection Date / Time</h2>

              <p class="primary--text font-weight-bold">We will provide a more specific (3 hour) time slot the evening before collection.</p>

              <v-alert
                v-if="showLaterDateBanner"
                text
                dense
                border="top"
                color="primary"
              >
                <p class="mb-0 pb-0 mt-1">We're a bit busier than normal at the moment, the next available collection date is <strong>{{ firstWeekCollectionDays[0].date.format('ddd Do MMM') }}</strong></p>
              </v-alert>

              <TimeslotPicker
                :date.sync="form.collectionDate"
                :time.sync="form.collectionTimeWindow"
                :dates="firstWeekCollectionDays"
                :times="collectionAvaliableSlots"
                :pickerConfig="{
                  min: pickerMinCollectionDate,
                  all: pickerAllowedCollectionDates
                }"
                type="Collection"
                mode="week"
                class="mb-4"
              />

              <v-divider class="my-8" />

              <h2 class="">Return Date</h2>

              <p class="primary--text font-weight-bold">We will provide a more specific (3 hour) time slot the evening before return.</p>

              <TimeslotPicker
                :date.sync="form.returnDate"
                :time.sync="form.returnTimeWindow"
                :dates="firstWeekReturnDays"
                :times="returnAvaliableSlots"
                type="Return"
                mode="week"
                class="mb-4"
              />

              <v-btn
                v-if="!form.alternateReturnPostcode"
                text
                small
                dense
                outlined
                color="primary"
                @click="updateBookingFormValue(true, 'alternateReturnPostcode')"
              >
                Return to a different address?
              </v-btn>

              <v-btn
                v-else
                text
                small
                dense
                outlined
                color="primary"
                @click="updateBookingFormValue(false, 'alternateReturnPostcode');
                updateBookingFormValue(null, 'returnAddress');
                updateBookingFormValue(null, 'returnPostcode');
                returnAddressData = {}"
              >
                Return to the collection address?
              </v-btn>

              <template v-if="form.alternateReturnPostcode">
                <h2 class="mt-6">Return Address</h2>
                <p>If you would like us to return the {{ form.bikesForRepair === 1 ? 'bike' : 'bikes' }} to a different address, please provide it below.</p>
                <v-row>
                  <v-col
                    class="py-0"
                    cols="12"
                    sm="6"
                  >
                    <v-text-field
                      :disabled="returnAddressData.addresses ? true : false"
                      :value="form.returnPostcode"
                      label="Return Postcode"
                      :rules="[v => !!v || 'Please choose a enter a return postcode']"
                      background-color="white"
                      required
                      outlined
                      dense
                      @input="updateBookingFormValue($event, 'returnPostcode')"/>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col class="pt-0">
                    <v-btn
                      v-if="returnAddressData.addresses ? false : true"
                      :loading="loading"
                      color="primary"
                      @click="checkAvailability('returnPostcode')">
                      Check Return Availability
                    </v-btn>
                  </v-col>
                </v-row>

                <v-row>
                  <v-col class="py-0">
                    <v-select
                      v-if="returnAddressData.addresses"
                      :value="form.returnAddress"
                      :items="returnAddressData.addresses"
                      :rules="[v => !!v || 'Please choose a return address']"
                      required
                      label="Return Address"
                      background-color="white"
                      outlined
                      dense
                      @change="updateBookingFormValue($event, 'returnAddress')"
                    />
                  </v-col>
                </v-row>
              </template>

              <v-divider class="mt-8" />

              <v-row>
                <v-col>
                  <v-btn
                    color="primary"
                    class="mt-4"
                    style="display: block; margin-left: auto;"
                    dark
                    x-large
                    @click="validateAndNext">
                    Next - Add Bike Details <v-icon right dark>mdi-chevron-right</v-icon>
                  </v-btn>
                </v-col>
              </v-row>
            </template>
            <template v-else>
              <v-alert
                outlined
                border="top"
                type="error"
              >
                Sorry we don't currently have any availability in {{ collectionAddressData.postcode_data.nuts }}. Please call us on 020 3397 0888
              </v-alert>
            </template>
          </template>
        </v-form>
      </v-col>
    </v-row>

    <v-dialog
      v-model="discountDialog"
      width="500"
    >
      <v-card>
        <v-card-title class="headline grey lighten-2">{{ discountDialogTitle }}</v-card-title>
        <v-card-text class="pt-4" v-html="discountDialogMessage" />
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            text
            @click="discountDialog = false"
          >
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>
<script>
import Banners from '@/components/Banners'
import formPage from '@/views/formPage'
import TimeslotPicker from '@/components/TimeslotPicker'
import find from 'lodash.find'
import moment from 'moment'
import filter from 'lodash.filter'
import ChooseLocationDialog from '@/components/ChooseLocationDialog'

export default {
  name: 'pageOne',

  components: {
    Banners,
    TimeslotPicker,
    ChooseLocationDialog
  },

  mixins: [formPage],

  data () {
    return {
      showCollectionAddressFields: false,
      chooseLocationDialog: false,
      loading: false,
      collectionPostcodeError: {
        errors: {
          postcode: []
        }
      },
      returnPostcodeError: {
        errors: {
          postcode: []
        }
      },
      collectionAddressData: {},
      returnAddressData: {},
      validForm: true,
      discountDialog: false,
      discountDialogTitle: null,
      discountDialogMessage: null
    }
  },
  mounted () {
    this.trackCheckoutStep1()
    if (this.$route.query.postcode) {
      this.updateBookingFormValue(this.$route.query.postcode, 'collectionPostcode')
    }
    if (this.$route.query.service) {
      this.$store.commit('setDefaultService', this.$route.query.service)
    }
    if (this.$route.query.promo) {
      this.$store.commit('setPromoToShow', this.$route.query.promo)
    }
  },
  watch: {
    'form.collectionDate': function (value) {
      var dates = filter(this.firstWeekReturnDays, returnDay => {
        // Only required for 5 day working week
        if (moment(value).day() === 5 && returnDay.date.day() === 1) {
          return false
        }
        return returnDay.available
      })
      if (dates[0]) {
        this.updateBookingFormValue(dates[0].date.format('YYYY-MM-DD'), 'returnDate')
      }
    },

    'form.returnDate': function () {
      // this.updateBookingFormValue('ANY', 'returnTimeWindow')
    },

    'returnAddressData': {
      deep: true,
      handler () {
        this.updateBookingFormValue('ANY', 'returnTimeWindow')
      }
    }
  },
  computed: {
    showLaterDateBanner () {
      return moment().add(1, 'day').format('YYYY-MM-DD') !== this.firstWeekCollectionDays[0].date.format('YYYY-MM-DD')
    },
    pickerMinCollectionDate () {
      return moment().add(2, 'day').format('YYYY-MM-DD')
    },
    firstWeekCollectionDays () {
      var days = []
      this.collectionAddressData.collection_days.forEach(day => {
        if (day.available) {
          days.push({ ...day, ...{ date: moment(day.date) } })
        }
      })
      return days.slice(0, 10)
    },
    firstWeekReturnDays () {
      var days = []
      this.collectionAddressData.return_days.forEach((day) => {
        var momentDate = moment(day.date)
        if (moment(this.form.collectionDate).add(2, 'day') < momentDate) {
          // Check Only required for 5 day working week
          if (!(moment(this.form.collectionDate).day() === 5 && momentDate.day() === 1)) {
            days.push({ ...day, ...{ date: momentDate } })
          }
        }
      })
      return days.slice(0, 7)
    },
    collectionAvaliableSlots () {
      return Object.values(find(this.collectionAddressData.collection_days, (day) => {
        return moment(day.date).format('YYYY-MM-DD') === this.form.collectionDate
      })?.slots || {})
    },
    returnAvaliableSlots () {
      return Object.values(find(this.returnAddressData.collection_days || this.collectionAddressData.collection_days, (day) => {
        return moment(day.date).format('YYYY-MM-DD') === this.form.collectionDate
      })?.slots || {})
    }
  },
  methods: {
    trackCheckoutStep1 () {
      window.dataLayer.push({
        event: 'checkout',
        ecommerce: {
          checkout: {
            actionField: { step: 1 }
          }
        }
      })
    },
    trackCheckoutStep2 () {
      window.dataLayer.push({
        event: 'checkout',
        ecommerce: {
          checkout: {
            actionField: { step: 2, option: `${this.form.bikesForRepair} bikes` }
          }
        }
      })
    },
    pickerAllowedCollectionDates (val) {
      var date = moment(val).format('YYYY-MM-DD')
      var dates = filter(this.collectionAddressData.collection_days, day => {
        return moment(day.date).format('YYYY-MM-DD') === date
      })
      if (dates[0]) {
        return dates[0].available
      }
      return false
    },
    checkAvailability (type) {
      var isCollection = type === 'collectionPostcode'
      var postcode = isCollection ? this.form.collectionPostcode : this.form.returnPostcode
      this.loading = true
      this.$api.persist('post', {
        path: 'availability',
        object: {
          postcode: postcode,
          number_of_bikes: this.form.bikesForRepair
        }
      })
        .then(({ data }) => {
          if (isCollection) {
            this.collectionPostcodeError = {
              errors: {
                postcode: []
              }
            }
            this.collectionAddressData = {
              postcode_data: data.postcode_data,
              collection_days: data.collection_days,
              return_days: data.return_days,
              addresses: data.addresses
            }
            this.checkForAutoDiscount(data.postcode_data)
            if (this.collectionAddressData.collection_days.length > 0 && this.collectionAddressData.return_days.length > 0 && !this.form.collectionDate) {
              this.trackCheckoutStep2()
              var availableCollectionDates = filter(this.collectionAddressData.collection_days, day => {
                return day.available === true
              })
              this.updateBookingFormValue(moment(availableCollectionDates[0].date).format('YYYY-MM-DD'), 'collectionDate')
            }
          } else {
            this.returnAddressData = {
              postcode_data: data.postcode_data,
              collection_days: data.collection_days,
              return_days: data.return_days,
              addresses: data.addresses
            }
            this.returnPostcodeError = {
              errors: {
                postcode: []
              }
            }
          }
        })
        .catch(error => {
          if (isCollection) {
            this.collectionAddressData = {}
            this.collectionPostcodeError = error
          } else {
            this.returnAddressData = {}
            this.returnPostcodeError = error
          }
        })
        .finally(() => { this.loading = false })
    },

    checkForAutoDiscount (postcodeData) {
      // City of London Discount Message
      if (postcodeData.admin_district === 'City of London') {
        this.discountDialog = true
        this.discountDialogTitle = 'City of London Discount'
        this.discountDialogMessage = 'havebike are working with the City of London Corporation to offer all residents 20% off our Regular or Deluxe service. Please use discount code <strong>CITY20COMMUNITY</strong> at checkout!'
      }
    },

    validateAndNext () {
      if (this.$refs.form.validate()) {
        this.$store.dispatch('setupBikes')
        this.$router.push({ name: 'pageTwo' })
      }
    }
  }
}
</script>
<style>
  .collection-picker__container,
  .return-picker__container {
    display: grid;
    grid-gap: 10px;
    grid-template-columns: 1fr;
    grid-auto-rows: auto;
  }
  @media screen and (min-width: 960px) {
    .collection-picker__container,
    .return-picker__container {
      grid-template-columns: repeat(2, 1fr);
    }
  }
</style>
